import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { GetVisitByIdVisitTypeEnum, VisitSummaryDataDto } from 'woofwoof-api';
import HtmlPdf from './HtmlPdf';
import { translateKey } from 'src/locales/translate';


// Register Open Sans font with full Latin Extended support (includes Polish)
Font.register({
  family: 'Open Sans',
  fonts: [
    {
      src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-italic.ttf',
      fontWeight: 400,
      fontStyle: 'italic',
    },
    {
      src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-600.ttf',
      fontWeight: 600,
    },
    {
      src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf',
      fontWeight: 700,
    },
  ],
});

// Register hyphenation callback
Font.registerHyphenationCallback((word) => [word]);

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 50,
    fontSize: 10,
    fontFamily: 'Open Sans',
  },
  header: {
    marginBottom: 20,
    borderBottom: '1 solid #000',
    paddingBottom: 10,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  organizationLogo: {
    width: 40,
    height: 40,
    marginRight: 10,
  },
  organizationName: {
    fontSize: 14,
    fontFamily: 'Open Sans',
    fontWeight: 700,
  },
  headerDetails: {
    flexDirection: 'column',
  },
  organizationDetails: {
    fontSize: 9,
    marginBottom: 2,
  },
  generatedInfo: {
    fontSize: 8,
    color: '#666',
    textAlign: 'center',
  },
  titleSection: {
    alignItems: 'center',
    marginBottom: 30,
  },
  title: {
    fontSize: 20,
    fontFamily: 'Open Sans',
    fontWeight: 700,
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 14,
    fontFamily: 'Open Sans',
    textAlign: 'center',
    marginBottom: 3,
  },
  twoColumnRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 15,
  },
  column: {
    width: '45%',
    display: 'flex',
    flexDirection: 'column',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: 'Open Sans',
    fontWeight: 700,
    marginBottom: 8,
    backgroundColor: '#EEEEEE',
  },
  field: {
    marginBottom: 4,
  },
  fieldLabel: {
    fontFamily: 'Open Sans',
    fontWeight: 700,
    fontSize: 8,
  },
  fieldValue: {
    fontFamily: 'Open Sans',
    fontSize: 8,
  },
  contentBlock: {
    marginBottom: 15,
  },
  contentTitle: {
    fontSize: 10,
    fontFamily: 'Open Sans',
    fontWeight: 700,
    marginBottom: 8,
    backgroundColor: '#EEEEEE',
  },
  contentText: {
    fontSize: 8,
    lineHeight: 1.5,
    textAlign: 'justify',
  },
  footer: {
    position: 'absolute',
    bottom: 50,
    left: 50,
    right: 50,
    borderTop: '1 solid #000',
    paddingTop: 10,
    fontSize: 8,
  },
});

interface SummaryMedication {
  name: string;
  dosage?: string;
  dosageInterval?: string;
}

interface SummaryTreatment {
  name: string;
  description?: string;
  treatmentDate?: string;
  priceNetto?: number;
  priceBrutto?: number;
  vatRate?: number;
}

interface VisitSummaryPDFProps {
  data: VisitSummaryDataDto;
}

const translateVisitType = (type: string): string => {
  const translations: Record<string, string> = {
    [GetVisitByIdVisitTypeEnum.Video]: 'Wizyta online',
    [GetVisitByIdVisitTypeEnum.Chat]: 'Wizyta online (Czat)',
    [GetVisitByIdVisitTypeEnum.Onsite]: 'Wizyta stacjonarna',
    [GetVisitByIdVisitTypeEnum.Mobile]: 'Wizyta mobilna',
  };
  return translations[type] || type;
};

export const VisitSummaryPDF = ({ data }: VisitSummaryPDFProps) => {
  const orgDetails: string[] = [];
  if (data.organizationAddress) {
    orgDetails.push(data.organizationAddress);
  }
  if (data.organizationZip && data.organizationCity) {
    orgDetails.push(`${data.organizationZip} ${data.organizationCity}`);
  } else if (data.organizationCity) {
    orgDetails.push(data.organizationCity);
  }
  if (data.organizationPhone) {
    orgDetails.push(`Tel: ${data.organizationPhone}`);
  }
  if (data.organizationEmail) {
    orgDetails.push(`Email: ${data.organizationEmail}`);
  }
  if (data.organizationNip) {
    orgDetails.push(`NIP: ${data.organizationNip}`);
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            {data.organizationLogoUrl && (
              <Image
                style={styles.organizationLogo}
                src={data.organizationLogoUrl}
              />
            )}
            <Text style={styles.organizationName}>{data.organizationName}</Text>
          </View>
          <Text style={styles.organizationDetails}>
            {orgDetails.join(' | ')}
          </Text>
        </View>

        {/* Title */}
        <View style={styles.titleSection}>
          <Text style={styles.subtitle}>
            Podsumowanie wizyty z dnia{' '}
            {format(new Date(data.visitDate), 'dd MMMM yyyy, HH:mm', {
              locale: pl,
            })}
          </Text>
          <Text style={styles.generatedInfo}>
            Wygenerowano przez {data.generatedBy} w systemie Vetsi Clinic w dniu{' '}
            {format(new Date(data.generatedAt), 'dd.MM.yyyy HH:mm', {
              locale: pl,
            })}
          </Text>
        </View>

        {/* Patient and Owner Information - Two Columns */}
        <View style={styles.twoColumnRow}>
          {/* Patient Information */}
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>PACJENT</Text>
            <View style={styles.field}>
              <Text>
                <Text style={styles.fieldLabel}>{translateKey("quickBooking.petNameSummary")}</Text>
                <Text style={styles.fieldValue}>{data.petName}</Text>
              </Text>
            </View>
            {data.petBreed && (
              <View style={styles.field}>
                <Text>
                  <Text style={styles.fieldLabel}>Rasa: </Text>
                  <Text style={styles.fieldValue}>{data.petBreed}</Text>
                </Text>
              </View>
            )}
            {data.petAge && (
              <View style={styles.field}>
                <Text>
                  <Text style={styles.fieldLabel}>{translateKey("visit.patientAgeLabel")}</Text>
                  <Text style={styles.fieldValue}>{data.petAge}</Text>
                </Text>
              </View>
            )}
            {data.petGender && (
              <View style={styles.field}>
                <Text>
                  <Text style={styles.fieldLabel}>{translateKey("visit.patientGenderLabel")}</Text>
                  <Text style={styles.fieldValue}>{data.petGender}</Text>
                </Text>
              </View>
            )}
          </View>

          {/* Owner Information */}
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>{translateKey("hardcoded.wlasciciel")}</Text>
            <View style={styles.field}>
              <Text>
                <Text style={styles.fieldLabel}>{translateKey("quickBooking.fullNameLabel")}</Text>
                <Text style={styles.fieldValue}>{data.ownerName}</Text>
              </Text>
            </View>
            {data.ownerEmail && (
              <View style={styles.field}>
                <Text>
                  <Text style={styles.fieldLabel}>{translateKey("quickBooking.emailLabel")}</Text>
                  <Text style={styles.fieldValue}>{data.ownerEmail}</Text>
                </Text>
              </View>
            )}
            {data.ownerPhone && (
              <View style={styles.field}>
                <Text>
                  <Text style={styles.fieldLabel}>{translateKey("quickBooking.phoneLabel")}</Text>
                  <Text style={styles.fieldValue}>{data.ownerPhone}</Text>
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Visit Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{translateKey("hardcoded.szczegoly_wizyty")}</Text>
          <View style={styles.field}>
            <Text>
              <Text style={styles.fieldLabel}>{translateKey("visit.bookingSummaryCategory")}</Text>
              <Text style={styles.fieldValue}>
                {translateVisitType(data.visitType)}
              </Text>
            </Text>
          </View>
          <View style={styles.field}>
            <Text>
              <Text style={styles.fieldLabel}>{translateKey("search.book.serviceLabelColon")}</Text>
              <Text style={styles.fieldValue}>{data.serviceName}</Text>
            </Text>
          </View>
          <View style={styles.field}>
            <Text>
              <Text style={styles.fieldLabel}>Specjalista: </Text>
              <Text style={styles.fieldValue}>{data.professionalName}</Text>
            </Text>
          </View>
        </View>

        {/* Visit Description */}
        {data.description && (
          <View style={styles.contentBlock}>
            <Text style={styles.contentTitle}>{translateKey("hardcoded.opis_wizyty_zgloszony_przez_wlasciciela")}</Text>
            <Text style={styles.contentText}>{data.description}</Text>
          </View>
        )}

        {/* Medications - own section, only when present */}
        {data.medications && data.medications.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>LEKARSTWA</Text>
            {(data.medications as SummaryMedication[]).map((med, idx) => (
              <View key={idx} style={{ marginBottom: 6 }}>
                <Text style={styles.fieldLabel}>{med.name}</Text>
                {med.dosage && (
                  <Text style={styles.fieldValue}>Dawka: {med.dosage}</Text>
                )}
                {med.dosageInterval && (
                  <Text style={styles.fieldValue}>
                    {translateKey("hardcoded.czestotliwosc")}{' '}
                    {med.dosageInterval === 'Day'
                      ? translateKey("quickBooking.medIntervalDay")
                      : med.dosageInterval === 'Week'
                      ? translateKey("quickBooking.medIntervalWeek")
                      : med.dosageInterval === 'Month'
                      ? translateKey("quickBooking.medIntervalMonth")
                      : med.dosageInterval}
                  </Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Treatments - own section, only when present */}
        {data.treatments && data.treatments.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>ZABIEGI</Text>
            {(data.treatments as SummaryTreatment[]).map((tr, idx) => (
              <View key={idx} style={{ marginBottom: 6 }}>
                <Text style={styles.fieldLabel}>{tr.name}</Text>
                {tr.treatmentDate && (
                  <Text style={styles.fieldValue}>
                    {translateKey("quickBooking.dateLabel")}{tr.treatmentDate}
                  </Text>
                )}
                {tr.description && (
                  <Text style={styles.fieldValue}>Opis: {tr.description}</Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Notes */}
        {data.notes && (
          <View style={styles.contentBlock}>
            <Text style={styles.contentTitle}>ZALECENIA I UWAGI</Text>
            <HtmlPdf html={data.notes} />
          </View>
        )}
      </Page>
    </Document>
  );
};
